import React, {useContext, useEffect, useMemo, useState} from 'react';
import * as Actions from '../../../../store/actions';
import {
  EmptyState,
  Loader,
  PageLimit,
  Pagination,
  SearchInput,
  TableLayout,
  SecondaryButton,
  DashboardLayout,
} from '../../../../components';
import {eye, lookupEmptySvg} from '../../../../assets/images/images';
import {connect} from 'react-redux';
import {useNavigate, useParams} from 'react-router-dom';
import moment from 'moment';
import {PageContext} from '../../../../helpers/context';
import {fetchEvents} from '../../../../requests/queries/flows';
import {shortenLargeNumbers} from '../../../../helpers/shortenLargeNumbers';
import {getStatusTextAndStyles} from '../../../../helpers/getStatusTextAndStyles';
import {filterOutEmptyValues} from '../../../../helpers';
import FilterV2 from 'components/FilterV2';
import PageNumber from 'components/PageNumber';
import CardSummary from '../../../../components/CardSummary';
import {useGatedQuery} from '../hooks/useGatedQuery';

function Tabs({activeTab, setActiveTab}) {
  return (
    <div className="border w-fit   p-1 border-[#E1E4EA] rounded-lg bg-[#F7F7F7] flex items-center gap-2 text-xs lg:text-tiny font-medium">
      <button
        className={`${
          activeTab === 'all'
            ? 'bg-white text-black'
            : 'bg-transparent text-[#868C98]'
        } cursor-pointer rounded-md px-4 py-[10px] transition hover:bg-white text-black`}
        onClick={() => setActiveTab('all')}
      >
        All Events
      </button>
      <button
        className={`${
          activeTab === 'block'
            ? 'bg-white text-black'
            : 'bg-transparent text-[#868C98]'
        } cursor-pointer rounded-md px-4 py-[10px] transition hover:bg-white text-black`}
        onClick={() => setActiveTab('block')}
      >
        Blocked Events
      </button>
    </div>
  );
}

function FlowsEvents() {
  const {slug} = useParams();
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    status: null,
    finalOutcome: null,
    transactionType: null,
    amount: {min: null, max: null},
    riskScore: {min: null, max: null},
    start: null,
    end: null,
    search: '',
  });
  const [currentPage, setCurrentPage] = useState('1');
  const [openFilter, setOpenFilter] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [limit, setLimit] = useState(10);
  const {setPageName} = useContext(PageContext);

  useEffect(() => {
    setPageName('easydetect');
  }, [setPageName]);

  const params = useMemo(
    () =>
      filterOutEmptyValues({
        // company_id: userDetails?.company?.id,
        limit,
        page: currentPage,
        status: activeTab === 'all' ? filters.status : activeTab,
        final_outcome: filters.finalOutcome,
        transaction_type: filters.transactionType,
        amount_min: filters.amount?.min,
        amount_max: filters.amount?.max,
        risk_score_min: filters.riskScore?.min,
        risk_score_max: filters.riskScore?.max,
        ...(filters.dateRange && {
          date_from: filters.dateRange.start,
          date_to: filters.dateRange.end,
        }),
        search: filters.search,
      }),
    [currentPage, filters, limit, activeTab],
  );

  const {data: flowEvents, isLoading} = useGatedQuery(
    ['flow-events', {params}],
    () => fetchEvents({params}),
  );

  const EVENTS = useMemo(
    () => (!isLoading && flowEvents && flowEvents?.rows) || [],
    [flowEvents, isLoading],
  );

  const pageClick = selected => {
    localStorage.setItem('page', String(selected));
    setCurrentPage(selected);
  };

  const handleFilter = selectedOptions => {
    const filterParams = filterOutEmptyValues(selectedOptions);
    setFilters({
      ...filterParams,
      search: filters.search,
    });
    setCurrentPage('1');
  };

  return (
    // <Layout pageTitle="Events" pageIcon={profileIc}>
    <DashboardLayout
      breadCrumbs={
        <p className="text-tiny font-normal text-deepBlue">Events</p>
      }
    >
      <div className="flex items-center flex-wrap py-8 gap-3 justify-between mx-[-32px] px-8 border border-transparent border-b-[#E4E4E6]">
        <Tabs activeTab={activeTab} setActiveTab={setActiveTab} />

        <div className="flex items-center gap-2 flex-wrap">
          <SearchInput
            onChange={value => {
              setFilters(prev => ({...prev, search: value}));
              setCurrentPage('1');
            }}
            placeholder="Search by transaction ID"
          />
          <SecondaryButton
            buttonText="Filter"
            onClick={() => setOpenFilter(prev => !prev)}
          />
        </div>
      </div>

      <div className={`filter-slide-container ${openFilter ? 'open' : ''}`}>
        <FilterV2
          filters={[
            {
              key: 'status',
              label: 'Status',
              type: 'select',
              options: [
                {value: 'pending', label: 'Pending'},
                {value: 'allow', label: 'Allow'},
                {value: 'block', label: 'Block'},
                {value: 'review', label: 'Review'},
              ],
              multiSelect: false,
            },
            {
              key: 'finalOutcome',
              label: 'Final Outcome',
              type: 'select',
              options: [
                {value: 'pending', label: 'Pending'},
                {value: 'allow', label: 'Allow'},
                {value: 'block', label: 'Block'},
                {value: 'review', label: 'Review'},
              ],
              multiSelect: false,
            },
            {
              key: 'transactionType',
              label: 'Transaction Type',
              type: 'select',
              options: [
                {value: 'deposit', label: 'Deposit'},
                {value: 'transfer', label: 'Transfer'},
              ],
              multiSelect: false,
            },
            {
              key: 'amount',
              label: 'Amount',
              type: 'range',
              options: {
                min: 0,
                max: 1000000,
                step: 100,
                inputType: 'fields',
              },
            },
            {
              key: 'riskScore',
              label: 'Risk Score',
              type: 'range',
              options: {
                min: 0,
                max: 100,
                step: 1,
                inputType: 'slider',
              },
            },
          ]}
          onFilterChange={handleFilter}
        />
      </div>

      <div className="pt-5 pb-6">
        <CardSummary
          title={<div className="capitalize">{activeTab} event count</div>}
          body={flowEvents?.count?.toLocaleString() || '0'}
          loading={isLoading}
        />
      </div>

      <section>
        <TableLayout negativeMargins>
          <thead className="font-semibold uppercase">
            <tr className="bg-white80">
              <th className="pl-4 sm:pl-6 xl:pl-8 h-11 font-medium text-left text-small text-[#5C688E] whitespace-nowrap">
                <div className="flex items-end gap-1">
                  <span>Event id</span>
                </div>
              </th>
              {/*<th className="pl-0 font-medium text-left h-11 text-small text-[#5C688E] whitespace-nowrap">*/}
              {/*  <div className="flex items-end gap-1">*/}
              {/*    <span>AMOUNT</span>*/}
              {/*  </div>*/}
              {/*</th>*/}
              <th className="pl-0 font-medium text-left h-11 text-small text-[#5C688E] whitespace-nowrap">
                <div className="flex items-end gap-1">
                  <span>score</span>
                </div>
              </th>
              <th className="pl-0 font-medium text-left h-11 text-small text-[#5C688E] whitespace-nowrap">
                <div className="flex items-end gap-1">
                  <span>Event type</span>
                </div>
              </th>
              <th className="pl-0 font-medium text-left h-11 text-small text-[#5C688E] whitespace-nowrap">
                <div className="flex items-end gap-1">
                  <span>Flow Name</span>
                </div>
              </th>
              <th className="pl-0 font-medium text-left h-11 text-small text-[#5C688E] whitespace-nowrap">
                <div className="flex items-end gap-1">
                  <span>Decision</span>
                </div>
              </th>
              <th className="pl-0 font-medium text-left h-11 text-small text-[#5C688E] whitespace-nowrap">
                <div className="flex items-end gap-1">
                  <span>Transaction Time</span>
                </div>
              </th>
              <th className="pl-0 font-medium text-left h-11 text-small text-[#5C688E] whitespace-nowrap"></th>
            </tr>
          </thead>

          <tbody>
            <tr>
              <td colSpan={8}>
                {isLoading ? (
                  <Loader height={45} />
                ) : !isLoading && EVENTS?.length === 0 ? (
                  <EmptyState
                    body="No event has been recorded yet. When there is, it will come up here"
                    noBtn
                    height={55}
                    src={lookupEmptySvg}
                  />
                ) : null}
              </td>
            </tr>
          </tbody>

          <tbody className="text-sm bg-white">
            {EVENTS &&
              EVENTS.map((event, i) => (
                <tr className="border-[0.5px] border-[#E4E3EC]" key={i}>
                  <td className="p-4 pl-4 sm:pl-6 xl:pl-8 text-body whitespace-nowrap capitalize">
                    {event?.transaction_id || '-'}
                  </td>
                  {/*<td className="p-4 pl-0 text-body whitespace-nowrap">*/}
                  {/*  {shortenLargeNumbers(event?.amount || 0, true, {*/}
                  {/*    startAt: 'B',*/}
                  {/*  })}*/}
                  {/*</td>*/}
                  <td className="p-4 pl-0 text-body whitespace-nowrap">
                    {shortenLargeNumbers(event?.score || 0, null, {
                      startAt: 'B',
                      decimals: 1,
                    })}
                  </td>
                  <td className="p-4 pl-0 text-body whitespace-nowrap">
                    <div
                      className={`uppercase py-1 px-2 text-xs font-medium ${
                        getStatusTextAndStyles(event?.transaction_type)?.solid
                      } rounded-full flex items-center w-fit`}
                    >
                      <span className="mb-[1px]">
                        {event?.transaction_type?.replaceAll('_', ' ') || '-'}
                      </span>
                    </div>
                  </td>
                  <td className="p-4 pl-0 text-body whitespace-nowrap">
                    {event?.project?.name || '-'}
                  </td>
                  <td className="p-4 pl-0 text-body whitespace-nowrap">
                    <div
                      className={`uppercase py-1 px-2 text-xs font-medium ${
                        getStatusTextAndStyles(event?.final_outcome)?.styles
                      } rounded-full flex items-center w-fit`}
                    >
                      <span className="mb-[1px]">
                        {event?.final_outcome || '-'}
                      </span>
                    </div>
                  </td>
                  <td className="p-4 pl-0 text-tiny text-body whitespace-nowrap">
                    {moment(event?.transaction_time).format(
                      'Do MMM, YYYY  HH:mm',
                    ) || '-'}
                  </td>
                  <td className="p-4 pl-0 text-body whitespace-nowrap">
                    <button
                      className="h-4 aspect-square"
                      onClick={() =>
                        navigate(`/flows/event/${slug}/details/${event.id}`)
                      }
                    >
                      <img src={eye} alt="" />
                    </button>
                  </td>
                </tr>
              ))}
          </tbody>
        </TableLayout>

        {!isLoading && EVENTS?.length !== 0 && (
          <div className="flex flex-wrap items-center justify-between gap-4 sm:gap-0 bg-white80 mx-[-32px] px-8 py-[18px] border-[0.5px] border-y-[#E4E3EC] border-transparent">
            <PageNumber
              currentPage={currentPage}
              limit={limit}
              totalCount={flowEvents?.count || 0}
            />

            <div className="flex items-center gap-5">
              <PageLimit
                onLimitChange={setLimit}
                total={flowEvents && flowEvents?.count}
                length={EVENTS?.length}
                limit={limit}
              />

              <Pagination
                total={flowEvents && Math.ceil(flowEvents?.count / limit)}
                current={+currentPage}
                onPageChange={activePage => {
                  localStorage.setItem('page', String(activePage));
                  pageClick(activePage);
                }}
              />
            </div>
          </div>
        )}
      </section>
    </DashboardLayout>
  );
}

export default connect(state => state, Actions)(FlowsEvents);
